import { isPlatformBrowser } from '@angular/common';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { map, tap, catchError, share } from 'rxjs/operators';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpRequest } from '@angular/common/http';
import { StorageService } from '@metromarkets/sdk';

import { ApiConfig } from '~shared/constants/api.config';

import { SOFT_LOGIN_TOKEN_KEY, TOKEN_LOCAL_STORAGE_KEY } from '../constants/identity.constant';
import { Token } from '../models/identity.model';
import { OAuthService } from 'angular-oauth2-oidc';

@Injectable()
export class TokenService {
  private refreshAccessRequest$: Observable<Token> | null;
  private readonly tokenSubject$: BehaviorSubject<Token | null>;
  private readonly localStorageKey = TOKEN_LOCAL_STORAGE_KEY;

  constructor(
    private httpClient: HttpClient,
    private storageService: StorageService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private oauthService: OAuthService
  ) {
    let token;
    if (isPlatformBrowser(this.platformId)) {
      token = this.storageService.local.get(this.localStorageKey);
    } else {
      token = '';
    }
    this.tokenSubject$ = new BehaviorSubject(token as Token);
  }

  public hasToken(): Observable<boolean> {
    return this.tokenSubject$.pipe(
      map((t) => {
        if (isPlatformBrowser(this.platformId)) {
          const softLoginToken = localStorage.getItem(SOFT_LOGIN_TOKEN_KEY);
          return Boolean(softLoginToken || t);
        }
        return Boolean(t);
      })
    );
  }

  public setToken(token: Token): void {
    this.tokenSubject$.next(token);
    if (isPlatformBrowser(this.platformId)) {
      this.storageService.local.set(this.localStorageKey, token);
    }
  }

  public removeToken(): void {
    this.tokenSubject$.next(null);
    if (isPlatformBrowser(this.platformId)) {
      this.storageService.local.remove(this.localStorageKey);
    }
  }

  public updateToken(): Observable<Token> {
    const token = this.tokenSubject$.getValue();

    if (this.refreshAccessRequest$) {
      return this.refreshAccessRequest$;
    } else if (token && token.refresh_token) {
      this.refreshAccessRequest$ = this.refreshAccess(token.refresh_token);
      return this.refreshAccessRequest$;
    }

    return throwError("don't have a refreshToken");
  }

  public addAuthorizationHeader(request: HttpRequest<any>): HttpRequest<any> {
    const token = this.oauthService.getAccessToken() ?? localStorage.getItem(SOFT_LOGIN_TOKEN_KEY);
    const hasNotToken = !token;
    return hasNotToken
      ? request
      : request.clone({
          headers: request.headers.set('Authorization', `Bearer ${token}`),
        });
  }

  private refreshAccess(refreshToken: string): Observable<Token> {
    const apiEndpoint = `${ApiConfig.SVC_IMS_API_URL()}/accounts/auth/refresh-access`;
    return this.httpClient.post(apiEndpoint, { refresh_token: refreshToken }).pipe(
      tap((token: Token) => {
        this.refreshAccessRequest$ = null;
        this.setToken(token);
      }),
      catchError((error) => {
        this.removeToken();
        return throwError(error);
      }),
      share()
    );
  }
}
